// Zod schema for the mobile app config. The shape is shared by:
//   - The App Builder UI in apps/erp-web (validates as the admin edits)
//   - The mobile app in apps/erp-mobile (validates what comes back from the
//     server before applying it — defends against corrupt/older configs)
//   - The Supabase RPC layer (CHECK constraint defers to TS, but we still
//     validate on the client before insert)
//
// Versioning: when the shape changes incompatibly, bump `schemaVersion` and
// add a migration step in `migrate.ts`. Older clients should fall back to
// defaults rather than crash.

import { z } from 'zod';
import { MODULE_KEYS } from '@erp/data-access/interfaces';

const localizedString = z.object({
  fr: z.string().min(1),
  ar: z.string().optional(),
  en: z.string().optional(),
});
export type LocalizedString = z.infer<typeof localizedString>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a 6-digit hex color');

/* -------------------- Identity -------------------- */
export const identitySchema = z.object({
  name: localizedString,
  shortName: z.string().min(1).max(20),
  /** Public URL of the logo asset (uploaded via storage service). */
  logoUrl: z.string().url().nullable(),
  /** Dark-mode variant of the logo. Falls back to logoUrl when null. */
  logoDarkUrl: z.string().url().nullable(),
  /** URL of a 1024x1024 splash image. */
  splashLogoUrl: z.string().url().nullable(),
  splashBgColor: hexColor,
  tagline: localizedString.partial({ fr: true }).optional(),
});
export type Identity = z.infer<typeof identitySchema>;

/* -------------------- Theme -------------------- */
export const themeSchema = z.object({
  primaryColor: hexColor,
  mode: z.enum(['light', 'dark', 'system']),
  radius: z.number().min(0).max(24),
  font: z.enum(['Geist', 'Inter', 'System']),
  density: z.enum(['comfortable', 'compact']),
});
export type Theme = z.infer<typeof themeSchema>;

/* -------------------- Bottom navigation -------------------- */
export const bottomNavItemSchema = z.object({
  /** Stable client-generated id so reorder/edit doesn't drop state. */
  id: z.string().min(1),
  /** Lucide icon name (or 'fab' for the centred FAB cutout). */
  icon: z.string().min(1),
  label: localizedString,
  /** ScreenId from the catalog — validated at runtime against SCREEN_CATALOG. */
  destination: z.string().min(1),
  /** Optional tint for active state; falls back to theme.primaryColor. */
  color: hexColor.optional(),
  /** Center FAB-style cutout (POS-style). At most one per nav. */
  isFab: z.boolean().default(false),
  /** Long-press shortcuts that open a popover with these screens. */
  quickActions: z.array(z.object({
    icon: z.string(),
    labelKey: z.string(),
    destination: z.string(),
  })).default([]),
});
export type BottomNavItem = z.infer<typeof bottomNavItemSchema>;

export const bottomNavSchema = z.array(bottomNavItemSchema).min(3).max(5);

/* -------------------- Per-screen tuning -------------------- */
export const screenConfigSchema = z.object({
  visible: z.boolean().default(true),
  /** Selected list-view columns (subset of ScreenMeta.columns). */
  columns: z.array(z.string()).optional(),
  /** Default filter values applied on open. Free-form per screen. */
  defaultFilters: z.record(z.string(), z.unknown()).optional(),
  /** Visible actions (subset of ScreenMeta.actions). */
  actions: z.array(z.string()).optional(),
  /** Show stats header on list views. */
  showStats: z.boolean().default(true),
});
export type ScreenConfig = z.infer<typeof screenConfigSchema>;

/* -------------------- Shortcuts (FAB / dashboard) -------------------- */
export const shortcutSchema = z.object({
  id: z.string(),
  icon: z.string(),
  labelKey: z.string(),
  /** Target screen id; arbitrary query params via `?key=value` suffix. */
  target: z.string(),
  /** Tile colour on the dashboard. */
  color: hexColor.optional(),
});
export type Shortcut = z.infer<typeof shortcutSchema>;

/* -------------------- Roles & permissions -------------------- */
const moduleKey = z.enum(MODULE_KEYS);
export const roleConfigSchema = z.object({
  allowedModules: z.union([z.literal('all'), z.array(moduleKey)]),
  /** Override the default bottom nav for this role. Null = use tenant default. */
  bottomNavOverride: bottomNavSchema.nullable(),
});

/* -------------------- Modules -------------------- */
export const modulesSchema = z.object({
  sales: z.boolean().default(true),
  purchases: z.boolean().default(false),
  stock: z.boolean().default(false),
  payments: z.boolean().default(false),
  treasury: z.boolean().default(false),
  checks: z.boolean().default(false),
  withholding: z.boolean().default(false),
  storefront: z.boolean().default(false),
  caisse: z.boolean().default(false),
});
export type ModulesFlags = z.infer<typeof modulesSchema>;

/* -------------------- Top-level app config -------------------- */
export const appConfigSchema = z.object({
  schemaVersion: z.literal(1),
  identity: identitySchema,
  theme: themeSchema,
  bottomNav: bottomNavSchema,
  modules: modulesSchema,
  screens: z.record(z.string(), screenConfigSchema).default({}),
  shortcuts: z.array(shortcutSchema).max(8).default([]),
  roles: z.record(z.string(), roleConfigSchema).default({}),
  /** Optional per-language override for the bottom nav (not used yet). */
  bottomNavLocale: z.record(z.string(), bottomNavSchema).optional(),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

/** Per-user override — same shape but every field is optional. The runtime
 *  merges (tenantDefault, userOverride) → effectiveConfig. */
export const appConfigOverrideSchema = appConfigSchema.partial().extend({
  schemaVersion: z.literal(1),
});
export type AppConfigOverride = z.infer<typeof appConfigOverrideSchema>;

/* -------------------- Helpers -------------------- */

/** Safely parses an unknown value into a config, returning null if invalid.
 *  Used by the mobile app to defend against malformed server responses. */
export function safeParseConfig(value: unknown): AppConfig | null {
  const result = appConfigSchema.safeParse(value);
  return result.success ? result.data : null;
}

/** Merges a per-user override on top of a tenant default. Per-user values win
 *  field-by-field. Arrays (bottomNav, shortcuts) are replaced wholesale, not
 *  merged element-by-element — that's how users get to fully redesign their
 *  bottom nav without inheriting items they don't want. */
export function mergeOverride(base: AppConfig, override: AppConfigOverride | null): AppConfig {
  if (!override) return base;
  return {
    ...base,
    ...override,
    identity: override.identity ? { ...base.identity, ...override.identity } : base.identity,
    theme: override.theme ? { ...base.theme, ...override.theme } : base.theme,
    modules: override.modules ? { ...base.modules, ...override.modules } : base.modules,
    screens: { ...base.screens, ...(override.screens ?? {}) },
    bottomNav: override.bottomNav ?? base.bottomNav,
    shortcuts: override.shortcuts ?? base.shortcuts,
    roles: { ...base.roles, ...(override.roles ?? {}) },
  };
}
